/**
 * ReferralDetail
 *
 * Modal that fetches fresh data from GET /admin/agentic_payment_referral/find?id=:id
 * then displays all fields for the referral record.
 */

import React, { useState, useEffect } from 'react';
import { getRequest } from '../../utils/httpRequest';
import { API_ENDPOINTS } from './constants';
import { modalStyles, colors } from '../AgenticPaymentTransactionHistory/styles';
import { getStatusBadgeStyle, formatReferralDate } from './utils';
import ReferralTransactions from './ReferralTransactions';

// Map camelCase API response → snake_case keys the component uses
const mapApiResponse = (d) => ({
  id:                        d.id,
  referral_ext_id:           d.referralExtId,
  referrer_user_id:          d.referrerUserId,
  referee_user_id:           d.refereeUserId,
  referrer_email:            d.referrerEmail,
  referee_name:              d.refereeName,
  signup_date:               d.signupDate,
  country:                   d.country,
  reward_currency:           d.rewardCurrency,
  exchange_rate:             d.exchangeRateSnapshot,
  reward_amount:             d.convertedRewardAmount,
  base_reward_usd:           d.baseRewardUsd,
  has_performed_transaction: d.hasPerformedTransaction,
  referrer_has_transaction:  d.referrerHasTransaction,
  status:                    d.status,
  payout_preference:         d.payout_preference || d.payoutPreference,
  paid_at:                   d.paidAt,
  payment_tx_id:             d.paymentTxId,
  held_at:                   d.heldAt,
  held_reason:               d.heldReason,
  rejected_at:               d.rejectedAt,
  rejected_reason:           d.rejectedReason,
  admin_action_by:           d.adminActionBy,
  created_at:                d.createdAt,
  updated_at:                d.updatedAt,
});

// ── Detail helpers ────────────────────────────────────────────────────────────

const DetailRow = ({ label, children }) => (
  <div style={modalStyles.detailRow}>
    <span style={modalStyles.detailLabel}>{label}</span>
    <div style={modalStyles.detailValue}>{children}</div>
  </div>
);

const BoolBadge = ({ value }) => (
  <span style={{
    padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
    background: value ? '#d4edda' : '#f8d7da',
    color: value ? '#155724' : '#721c24',
  }}>
    {value ? 'Yes' : 'No'}
  </span>
);

// ── ReferralDetail ────────────────────────────────────────────────────────────

const ReferralDetail = ({ referral: initialReferral, adminUserId = '', onClose }) => {
  const [referral, setReferral]                 = useState(null);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState(null);
  const [showTransactions, setShowTransactions] = useState(false);

  const fetchDetail = (id) => {
    setLoading(true);
    setError(null);
    getRequest(API_ENDPOINTS.FIND(id))
      .then((data) => {
        if (!data.success) throw new Error(data.error || 'Failed to load referral details');
        setReferral(mapApiResponse(data.data));
      })
      .catch((err) => setError(err.message || 'Failed to load referral details'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!initialReferral?.id) return;
    fetchDetail(initialReferral.id);
  }, [initialReferral?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!initialReferral) return null;

  return (
    <>
      <div style={modalStyles.overlay} onClick={onClose} data-testid="referral-detail-overlay">
        <div style={modalStyles.content} onClick={(e) => e.stopPropagation()} data-testid="referral-detail-modal">

          {/* Header */}
          <div style={modalStyles.header}>
            <h3 style={modalStyles.headerTitle}>Referral Details</h3>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {!loading && !error && (
                <div
                  role="button" tabIndex={0}
                  onClick={() => setShowTransactions(true)}
                  onKeyDown={(e) => e.key === 'Enter' && setShowTransactions(true)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '8px 16px', borderRadius: '4px',
                    background: 'linear-gradient(135deg, #e58734, #d77a2a)',
                    color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                    userSelect: 'none',
                  }}
                >
                  View Transactions
                </div>
              )}
              <div
                role="button" tabIndex={0}
                onClick={onClose}
                onKeyDown={(e) => e.key === 'Enter' && onClose()}
                style={{ ...modalStyles.closeButton, cursor: 'pointer', userSelect: 'none' }}
                data-testid="referral-detail-close"
              >
                Close
              </div>
            </div>
          </div>

          {/* Body */}
          <div style={modalStyles.body}>
            {loading && (
              <div style={{ textAlign: 'center', padding: '48px 20px' }}>
                <div style={{ width: '32px', height: '32px', border: `3px solid ${colors.border}`, borderTopColor: colors.primary, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                <p style={{ color: colors.gray600, fontSize: '14px', margin: 0 }}>Loading referral details...</p>
              </div>
            )}

            {error && (
              <div style={{ padding: '24px', textAlign: 'center' }}>
                <p style={{ color: '#721c24', marginBottom: '12px' }}>{error}</p>
                <button
                  type="button"
                  onClick={() => fetchDetail(initialReferral.id)}
                  style={{ padding: '8px 16px', background: colors.primary, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}
                >
                  Retry
                </button>
              </div>
            )}

            {!loading && !error && referral && (
              <>
                <DetailRow label="ID">
                  <code style={{ background: colors.gray200, padding: '4px 8px', borderRadius: '3px', fontSize: '12px', fontFamily: 'monospace' }}>{referral.id}</code>
                </DetailRow>
                <DetailRow label="Referral Ext ID">
                  <code style={{ background: colors.gray200, padding: '4px 8px', borderRadius: '3px', fontSize: '12px', fontFamily: 'monospace' }}>{referral.referral_ext_id || '-'}</code>
                </DetailRow>
                <DetailRow label="Referrer User ID">
                  <code style={{ background: colors.gray200, padding: '4px 8px', borderRadius: '3px', fontSize: '12px', fontFamily: 'monospace' }}>{referral.referrer_user_id || '-'}</code>
                </DetailRow>
                <DetailRow label="Referrer Email">{referral.referrer_email || '-'}</DetailRow>
                <DetailRow label="Referee Name">{referral.referee_name || '-'}</DetailRow>
                <DetailRow label="Signup Date">{formatReferralDate(referral.signup_date)}</DetailRow>
                <DetailRow label="Country">{referral.country || '-'}</DetailRow>
                <DetailRow label="Reward Currency">{referral.reward_currency || '-'}</DetailRow>
                <DetailRow label="Exchange Rate">{referral.exchange_rate || '-'}</DetailRow>
                <DetailRow label="Reward Amount">
                  <strong style={{ fontSize: '16px', color: colors.primary }}>{referral.reward_amount || '-'}</strong>
                </DetailRow>
                <DetailRow label="Base Reward (USD)">{referral.base_reward_usd || '-'}</DetailRow>
                <DetailRow label="Referee Transacted"><BoolBadge value={referral.has_performed_transaction} /></DetailRow>
                <DetailRow label="Referrer Transacted"><BoolBadge value={referral.referrer_has_transaction} /></DetailRow>
                <DetailRow label="Status">
                  <span style={{ ...getStatusBadgeStyle(referral.status), padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>
                    {referral.status}
                  </span>
                </DetailRow>
                <DetailRow label="Payout Preference">
                  {referral.payout_preference ? (
                    <span style={{
                      padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
                      background: referral.payout_preference === 'local' ? '#d4edda' : '#cce5ff',
                      color: referral.payout_preference === 'local' ? '#155724' : '#004085',
                      textTransform: 'uppercase',
                    }}>
                      {referral.payout_preference}
                    </span>
                  ) : '-'}
                </DetailRow>
                <DetailRow label="Paid At">{formatReferralDate(referral.paid_at)}</DetailRow>
                <DetailRow label="Payment TX ID">
                  {referral.payment_tx_id
                    ? <code style={{ background: colors.gray200, padding: '4px 8px', borderRadius: '3px', fontSize: '12px', fontFamily: 'monospace' }}>{referral.payment_tx_id}</code>
                    : '-'}
                </DetailRow>
                {referral.held_at && <DetailRow label="Held At">{formatReferralDate(referral.held_at)}</DetailRow>}
                {referral.held_reason && (
                  <DetailRow label="Hold Reason">
                    <span style={{ color: '#7d5a00', background: '#ffeeba', padding: '4px 10px', borderRadius: '4px', fontSize: '13px' }}>{referral.held_reason}</span>
                  </DetailRow>
                )}
                {referral.rejected_at && <DetailRow label="Rejected At">{formatReferralDate(referral.rejected_at)}</DetailRow>}
                {referral.rejected_reason && (
                  <DetailRow label="Reject Reason">
                    <span style={{ color: '#721c24', background: '#f8d7da', padding: '4px 10px', borderRadius: '4px', fontSize: '13px' }}>{referral.rejected_reason}</span>
                  </DetailRow>
                )}
                <DetailRow label="Created At">{formatReferralDate(referral.created_at)}</DetailRow>
                <DetailRow label="Updated At">{formatReferralDate(referral.updated_at)}</DetailRow>
              </>
            )}
          </div>
        </div>
      </div>

      {showTransactions && referral && (
        <ReferralTransactions
          referralId={referral.id}
          referralExtId={referral.referral_ext_id}
          onClose={() => setShowTransactions(false)}
        />
      )}
    </>
  );
};

export default ReferralDetail;
